
import React, { useState } from 'react';
import Button from '../base/Button';
import Modal from '../base/Modal';
import { categories, valueBands, statuses } from '../../mocks/procurements';

interface FilterBarProps {
  filters: {
    cities: string[];
    categories: string[];
    valueBands: string[];
    dateRange: { start: string; end: string };
    statuses: string[];
    sortBy: string;
  };
  onFiltersChange: (filters: any) => void;
  onSaveSearch: () => void;
  onSubscribe: () => void;
  onClearFilters: () => void;
}

export default function FilterBar({
  filters,
  onFiltersChange,
  onSaveSearch,
  onSubscribe,
  onClearFilters,
}: FilterBarProps) {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showValueBandTooltip, setShowValueBandTooltip] = useState<string | null>(null);
  const [searchName, setSearchName] = useState('');
  const [subscribeFreq, setSubscribeFreq] = useState('weekly');
  const [subscribeType, setSubscribeType] = useState('email');

  const sortOptions = [
    { value: 'updated_desc', label: 'Viimeksi päivitetty' },
    { value: 'competition_asc', label: 'Aikaisin kilpailu' },
    { value: 'value_desc', label: 'Korkein arvoluokka' },
  ];

  const handleSaveSearch = () => {
    if (searchName.trim()) {
      // Mock save functionality
      setShowSaveModal(false);
      setSearchName('');
      alert('Haku tallennettu onnistuneesti!');
    }
  };

  const handleSubscribe = () => {
    // Mock subscribe functionality
    setShowSubscribeModal(false);
    alert(`Ilmoitukset aktivoitu: ${subscribeFreq} ${subscribeType}`);
  };

  return (
    <>
      <div className="sticky top-16 z-30 bg-white border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* City filter */}
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-700 mb-1">Kaupunki</label>
              <select
                value={filters.cities[0] || ''}
                onChange={(e) => {
                  onFiltersChange({ ...filters, cities: e.target.value ? [e.target.value] : [] });
                }}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 w-40 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
              >
                <option value="">Kaikki kaupungit</option>
                <option value="helsinki">Helsinki</option>
                <option value="tampere">Tampere</option>
                <option value="turku">Turku</option>
                <option value="oulu">Oulu</option>
                <option value="jyvaskyla">Jyväskylä</option>
                <option value="lahti">Lahti</option>
                <option value="kuopio">Kuopio</option>
                <option value="pori">Pori</option>
                <option value="kouvola">Kouvola</option>
                <option value="kotka">Kotka</option>
              </select>
            </div>

            {/* Category filter */}
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-700 mb-1">Kategoria</label>
              <select
                value={filters.categories[0] || ''}
                onChange={(e) => {
                  onFiltersChange({ ...filters, categories: e.target.value ? [e.target.value] : [] });
                }}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 w-48 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
              >
                <option value="">Kaikki kategoriat</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Value band filter */}
            <div className="flex flex-col relative">
              <label className="text-xs font-medium text-gray-700 mb-1">Arvoluokka</label>
              <div className="flex gap-2">
                {valueBands.map((band) => (
                  <div key={band.id} className="relative">
                    <button
                      onClick={() => {
                        const newBands = filters.valueBands.includes(band.id)
                          ? filters.valueBands.filter(b => b !== band.id)
                          : [...filters.valueBands, band.id];
                        onFiltersChange({ ...filters, valueBands: newBands });
                      }}
                      onMouseEnter={() => setShowValueBandTooltip(band.id)}
                      onMouseLeave={() => setShowValueBandTooltip(null)}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors cursor-pointer ${
                        filters.valueBands.includes(band.id)
                          ? 'bg-blue-100 border-blue-300 text-blue-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                      aria-label={`Arvoluokka ${band.name}: ${band.description}`}
                    >
                      {band.name}
                    </button>
                    
                    {showValueBandTooltip === band.id && (
                      <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50">
                        {band.description}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Status filter */}
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-700 mb-1">Tila</label>
              <select
                value={filters.statuses[0] || ''}
                onChange={(e) => {
                  onFiltersChange({ ...filters, statuses: e.target.value ? [e.target.value] : [] });
                }}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 w-32 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
              >
                <option value="">Kaikki tilat</option>
                {statuses.map((status) => (
                  <option key={status.id} value={status.id}>{status.name}</option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-700 mb-1">Järjestä</label>
              <select
                value={filters.sortBy}
                onChange={(e) => onFiltersChange({ ...filters, sortBy: e.target.value })}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 w-40 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-end gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={() => setShowSaveModal(true)}>
                <i className="ri-save-line mr-2 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                Tallenna haku
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowSubscribeModal(true)}>
                <i className="ri-notification-line mr-2 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                Tilaa ilmoitukset
              </Button>
              <button
                onClick={onClearFilters}
                className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg cursor-pointer"
                aria-label="Tyhjennä suodattimet"
              >
                <i className="ri-filter-off-line text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              </button>
              <a
                href="/rss"
                className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg cursor-pointer"
                aria-label="RSS-syöte"
              >
                <i className="ri-rss-line text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Save Search Modal */}
      <Modal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title="Tallenna haku"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="searchName" className="block text-sm font-medium text-gray-700 mb-1">
              Haun nimi
            </label>
            <input
              type="text"
              id="searchName"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Anna haulle nimi..."
            />
          </div>
          
          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Aktiiviset suodattimet:</h4>
            <div className="text-sm text-gray-600">
              {filters.cities.length > 0 && <p>Kaupungit: {filters.cities.join(', ')}</p>}
              {filters.categories.length > 0 && <p>Kategoriat: {filters.categories.join(', ')}</p>}
              {filters.valueBands.length > 0 && <p>Arvoluokat: {filters.valueBands.join(', ')}</p>}
              {filters.statuses.length > 0 && <p>Tilat: {filters.statuses.join(', ')}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowSaveModal(false)}>
              Peruuta
            </Button>
            <Button onClick={handleSaveSearch}>
              Tallenna
            </Button>
          </div>
        </div>
      </Modal>

      {/* Subscribe Modal */}
      <Modal
        isOpen={showSubscribeModal}
        onClose={() => setShowSubscribeModal(false)}
        title="Tilaa ilmoitukset"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ilmoitustiheys
            </label>
            <div className="space-y-2">
              {[
                { value: 'immediate', label: 'Välittömästi' },
                { value: 'daily', label: 'Päivittäin' },
                { value: 'weekly', label: 'Viikoittain' }
              ].map((freq) => (
                <label key={freq.value} className="flex items-center">
                  <input
                    type="radio"
                    name="frequency"
                    value={freq.value}
                    checked={subscribeFreq === freq.value}
                    onChange={(e) => setSubscribeFreq(e.target.value)}
                    className="mr-2"
                  />
                  {freq.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Toimitus
            </label>
            <div className="space-y-2">
              {[
                { value: 'email', label: 'Sähköposti' },
                { value: 'rss', label: 'RSS-syöte' },
                { value: 'ics', label: 'Kalenteritiedosto (ICS)' }
              ].map((type) => (
                <label key={type.value} className="flex items-center">
                  <input
                    type="radio"
                    name="delivery"
                    value={type.value}
                    checked={subscribeType === type.value}
                    onChange={(e) => setSubscribeType(e.target.value)}
                    className="mr-2"
                  />
                  {type.label}
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowSubscribeModal(false)}>
              Peruuta
            </Button>
            <Button onClick={handleSubscribe}>
              Tilaa
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
